// bd-deepcontext-b4zw — the debounced, abortable URL-pool estimate from
// ScheduledJobs.tsx. Dependencies stay the individual filter fields (not the
// whole formData object) so typing the job name still does not refetch.
import { useCallback, useEffect, useRef, useState } from "react";
import type { ScheduledJobFormData } from "../scheduledJobFormDefaults";
import { scheduledJobsApi } from "./api";
import { buildPoolEstimateParams } from "./formPayload";
import type { UrlPoolEstimate } from "./types";

const ESTIMATE_DEBOUNCE_MS = 500;

export function usePoolEstimate(
  formData: ScheduledJobFormData,
  isFormOpen: boolean,
): { poolEstimate: UrlPoolEstimate | null; estimateLoading: boolean } {
  const [poolEstimate, setPoolEstimate] = useState<UrlPoolEstimate | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPoolEstimate = useCallback(() => {
    if (formData.promptIds.length === 0) {
      setPoolEstimate(null);
      return;
    }
    if (!isFormOpen) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setEstimateLoading(true);

    scheduledJobsApi
      .estimateUrlPool(buildPoolEstimateParams(formData), controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setPoolEstimate(result);
          setEstimateLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setPoolEstimate(null);
          setEstimateLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.urlSourceMode,
    formData.countryCodes,
    formData.promptIds,
    formData.similaritySelectivity,
    formData.countryDistributionMode,
    formData.selectedDomainInfos,
    formData.domainExtensions,
    formData.excludeHomepages,
    isFormOpen,
  ]);

  useEffect(() => {
    if (formData.promptIds.length === 0 || !isFormOpen) {
      setPoolEstimate(null);
      return;
    }
    const timer = setTimeout(fetchPoolEstimate, ESTIMATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPoolEstimate]);

  return { poolEstimate, estimateLoading };
}
